import React, { useState, useMemo, useEffect } from "react";
import { Modal, Form, Input, ConfigProvider, Select, Card, Alert, Button } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { fetchAllIdClientsAction } from "../../slices/internalToolSlice";
import { hasRequiredScopes } from "../../utils/roleUtils";
import { isDashmoteStaffEmail } from "../../utils/isDashmoteStaffEmail";
import ScopePicker from "@web/components/InternalConfigurationTable/inviteUsers/ScopePicker";
import AccountTypeSelect from "../AccountTypeSelect";
import DefaultWorkspaceSelect from "../DefaultWorkspaceSelect";
import { emptyProductScopes } from "../productScopes";
import { useDefaultWorkspace } from "../../hooks/useDefaultWorkspace";
import "./InviteUsersModal.css";

const { Option } = Select;

const CreateUserModal = ({
  open,
  onOk,
  onCancel,
  form,
  className = "custom-invite-modal",
  width = 720,
  // When Create hits a deactivated email, the page passes that email here
  // to offer reactivation-with-the-entered-scope instead of a dead-end error.
  reactivateEmail = null,
  reactivating = false,
  onReactivate,
}) => {
  const dispatch = useDispatch();
  const { clientIds } = useSelector((state) => state.internalTool);
  const { hierarchicalWorkspaces } = useSelector((state) => state.workspace);

  useEffect(() => {
    dispatch(fetchAllIdClientsAction());
  }, [dispatch]);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  // Staff-only account classification: null (regular) | "demo" | "test".
  // Dashmote-admin only; the backend re-gates on the caller and enforces
  // demo⇒@dashmote.com.
  const [accountType, setAccountType] = useState(null);
  const [workspaceAssignments, setWorkspaceAssignments] = useState([
    {
      id: 1,
      workspaceId: null,
      role: "normal_user",
      product_scopes: emptyProductScopes(),
    },
  ]);

  const {
    assignedWorkspaces,
    effectiveDefault,
    pick: pickDefaultWorkspace,
    reset: resetDefaultWorkspace,
    discardIfUnassigned: discardDefaultIfUnassigned,
  } = useDefaultWorkspace(workspaceAssignments);

  // Reset the form each time the modal opens so a reopen never shows stale
  // input from a prior session (parity with InviteUsersModal).
  useEffect(() => {
    if (open) {
      setEmail("");
      setName("");
      setAccountType(null);
      resetDefaultWorkspace();
      setWorkspaceAssignments([
        {
          id: 1,
          workspaceId: null,
          role: "normal_user",
          product_scopes: emptyProductScopes(),
        },
      ]);
    }
  }, [open, resetDefaultWorkspace]);

  // If Demo was selected and the email is no longer @dashmote.com, revert to
  // Regular — demo is only valid on the staff domain (the backend rejects it),
  // so don't leave a stale demo selection that would 400 on submit.
  useEffect(() => {
    if (accountType === "demo" && !isDashmoteStaffEmail(email)) {
      setAccountType(null);
    }
  }, [email, accountType]);

  // Markets come from the workspace's page-level scope (hierarchicalWorkspaces),
  // matching User Scope Management — not a per-workspace markets fetch.
  const getMarketsForWorkspace = (workspaceId) => {
    const client = hierarchicalWorkspaces?.[workspaceId];
    if (!client) return [];
    const markets = [];
    Object.values(client.regions || {}).forEach((region) => {
      region.markets.forEach((m) => {
        if (!markets.includes(m)) markets.push(m);
      });
    });
    return markets;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const addWorkspaceAssignment = () => {
    const newId = Math.max(...workspaceAssignments.map((w) => w.id)) + 1;
    setWorkspaceAssignments([
      ...workspaceAssignments,
      {
        id: newId,
        workspaceId: null,
        role: "normal_user",
        product_scopes: emptyProductScopes(),
      },
    ]);
  };

  const removeWorkspaceAssignment = (id) => {
    if (workspaceAssignments.length > 1) {
      const remaining = workspaceAssignments.filter((w) => w.id !== id);
      setWorkspaceAssignments(remaining);
      discardDefaultIfUnassigned(remaining);
    }
  };

  const updateWorkspaceAssignment = (id, field, value) => {
    const next = workspaceAssignments.map((w) => {
      if (w.id === id) {
        const updatedAssignment = { ...w, [field]: value };

        // Changing role to Super User (full access) or switching workspace
        // (markets differ) resets the per-product market scope.
        if (
          (field === "role" && value === "super_admin") ||
          field === "workspaceId"
        ) {
          updatedAssignment.product_scopes = emptyProductScopes();
        }

        return updatedAssignment;
      }
    return w;
    });
    setWorkspaceAssignments(next);
    // Re-pointing a row away from the picked workspace invalidates the pick the
    // same way deleting the row does, so discard it here too. Without this the
    // pick resurfaces as the default when the row is pointed back.
    if (field === "workspaceId") {
      discardDefaultIfUnassigned(next);
    }
  };

  // Super User maps to super_admin server-side (no product_scopes needed);
  // every other role carries its per-product market scope as-is. Shared by the
  // Create submit and the reactivate offer so both act on the CURRENT form.
  const buildFormData = () => ({
    email: email,
    name: name && name.trim() !== "" ? name.trim() : null,
    workspaceAssignments: workspaceAssignments.filter((w) => w.workspaceId),
    accountType,
    defaultWorkspaceId: effectiveDefault,
  });

  const handleOk = () => {
    if (!isValidEmail(email)) {
      return;
    }
    onOk(buildFormData());
  };

  // Every non-super assignment must grant ≥1 market, or the backend writes
  // product_scopes: null and the user 403s on dashboard load. Gates
  // both Create and the Reactivate offer (which replaces scope with this same
  // configuration — offering it with nothing configured would strip access).
  const scopeIncomplete =
    workspaceAssignments.filter((w) => w.workspaceId).length === 0 ||
    workspaceAssignments.some((w) => w.workspaceId && !hasRequiredScopes(w));

  const handleCancel = () => {
    setEmail("");
    setName("");
    setAccountType(null);
    resetDefaultWorkspace();
    setWorkspaceAssignments([
      {
        id: 1,
        workspaceId: null,
        role: "normal_user",
        product_scopes: emptyProductScopes(),
      },
    ]);
    onCancel();
  };

  const clientIdList = useMemo(() => {
    if (Array.isArray(clientIds)) return clientIds;
    return clientIds?.id_client || [];
  }, [clientIds]);

  const workspaceOptions = clientIdList.map((id) => ({
    value: id,
    label: id,
  }));

  return (
    <ConfigProvider
      theme={{
        components: {
          TreeSelect: { borderRadius: 16 },
          Modal: { borderRadiusLG: 16 },
        },
      }}
    >
      <Modal
        title={
          <div className="flex justify-between items-center pb-4 border-b border-gray-100">
            <h2 className="text-gray-900 text-xl font-semibold font-['Inter'] leading-tight">
              Create User
            </h2>
          </div>
        }
        open={open}
        onOk={handleOk}
        onCancel={handleCancel}
        width={width}
        centered
        className={className}
        maskClosable={false}
        okText="Create User"
        cancelText="Cancel"
        okButtonProps={{
          disabled: !isValidEmail(email) || scopeIncomplete,
          className:
            "bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700 rounded-lg h-10 px-6 font-medium",
        }}
        cancelButtonProps={{
          className:
            "border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700 rounded-lg h-10 px-6",
        }}
      >
        <div className="py-6 flex flex-col gap-6">
          {/* Only while the email still matches the one that triggered the
              offer — editing the email dismisses it (re-Create to re-check). */}
          {reactivateEmail && email === reactivateEmail && (
            <Alert
              type="warning"
              showIcon
              message={`${reactivateEmail} is deactivated, not deleted`}
              description={
                <div className="flex flex-col gap-3">
                  <span>
                    An account for this email already exists but was deactivated.
                    You can reactivate it and grant the access you configured
                    above — no new account is created.
                  </span>
                  <div>
                    <Button
                      type="primary"
                      loading={reactivating}
                      disabled={scopeIncomplete}
                      onClick={() => onReactivate(buildFormData())}
                    >
                      Reactivate &amp; grant the configured access
                    </Button>
                    {scopeIncomplete && (
                      <div className="text-xs text-gray-500 mt-1">
                        Configure a workspace and market scope above to enable
                        reactivation.
                      </div>
                    )}
                  </div>
                </div>
              }
            />
          )}
          <Form
            form={form}
            layout="vertical"
            className="w-full flex flex-col gap-6"
          >
            {/* Email Field */}
            <div className="flex flex-col gap-2">
              <label className="text-[#3f3f46] text-sm font-bold font-['Inter']">
                Email Address
              </label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address..."
                className="rounded-lg"
                size="large"
                status={email && !isValidEmail(email) ? "error" : ""}
              />
              {email && !isValidEmail(email) && (
                <span className="text-xs text-red-500">
                  Please enter a valid email address
                </span>
              )}
            </div>

            {/* Name Field */}
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <label className="text-[#3f3f46] text-sm font-bold font-['Inter']">
                  Name
                </label>
                <span className="text-xs text-gray-500 font-['Inter']">
                  Optional
                </span>
              </div>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter user name (optional)..."
                className="rounded-lg"
                size="large"
              />
            </div>

            {/* Account type — staff-only classification. Regular =
                normal user; Test = fixture hidden from client user lists; Demo
                scopes a @dashmote.com staff account (so it's only selectable
                for a @dashmote.com email). Backend gates to Dashmote admins. */}
            <Card
              size="small"
              style={{
                marginBottom: "1rem",
                borderRadius: "12px",
                border: "2px solid #722ed1",
                background: "linear-gradient(to bottom, #f9f0ff, #ffffff)",
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <span className="text-[#262626] text-[15px] font-semibold font-['Inter']">
                    Account type
                  </span>
                  <div className="text-xs text-gray-500 font-['Inter'] mt-1 flex flex-col gap-0.5 max-w-md">
                    <span>
                      <span className="font-semibold">Regular</span> — real
                      users; regular Dashmote accounts see every workspace,
                      others only their assigned ones.
                    </span>
                    <span>
                      <span className="font-semibold">Demo</span> — Dashmote
                      email only, limited to its assigned workspaces.
                    </span>
                    <span>
                      <span className="font-semibold">Test</span> — an internal
                      account, hidden from client-facing user lists.
                    </span>
                  </div>
                </div>
                <AccountTypeSelect
                  value={accountType}
                  onChange={setAccountType}
                  demoAllowed={
                    isValidEmail(email) && isDashmoteStaffEmail(email)
                  }
                />
              </div>
            </Card>

            {/* Workspace Assignments */}
            <div className="flex flex-col gap-3">
              <div className="flex justify-between items-center">
                <label className="text-[#3f3f46] text-sm font-bold font-['Inter']">
                  Workspace Assignments
                </label>
                <button
                  type="button"
                  onClick={addWorkspaceAssignment}
                  className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-sm font-medium rounded-lg transition-all duration-200 border border-transparent hover:border-blue-200"
                >
                  <PlusOutlined className="text-xs" />
                  <span>Add Workspace</span>
                </button>
              </div>

              {/* Workspace Assignment Cards */}
              <div className="flex flex-col gap-4">
                {workspaceAssignments.map((assignment) => (
                  <Card
                    key={assignment.id}
                    className="border border-gray-200 hover:border-gray-300 transition-colors duration-200 rounded-lg shadow-sm hover:shadow-md"
                    size="small"
                  >
                    <div className="flex flex-col gap-4">
                      {/* Workspace Selection and Delete Button */}
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1">
                          <label className="text-gray-700 text-xs font-medium font-['Inter'] mb-2 block">
                            Workspace
                          </label>
                          <Select
                            placeholder="Select a workspace"
                            value={assignment.workspaceId}
                            onChange={(value) =>
                              updateWorkspaceAssignment(
                                assignment.id,
                                "workspaceId",
                                value
                              )
                            }
                            className="w-full"
                            style={{ borderRadius: "8px" }}
                          >
                            {workspaceOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        </div>
                        {workspaceAssignments.length > 1 && (
                          <button
                            type="button"
                            onClick={() =>
                              removeWorkspaceAssignment(assignment.id)
                            }
                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-2 mt-6 rounded-lg transition-all duration-200"
                            title="Remove workspace assignment"
                          >
                            <DeleteOutlined />
                          </button>
                        )}
                      </div>

                      {/* Role Selection */}
                      <div className="flex flex-col gap-2">
                        <label className="text-gray-700 text-xs font-medium font-['Inter']">
                          Role for this workspace
                        </label>
                        <div className="flex flex-col gap-1">
                          {[
                            {
                              value: "super_admin",
                              label: "Super Admin",
                              description:
                                "Full workspace access and user management",
                            },
                            {
                              value: "admin",
                              label: "Admin",
                              description:
                                "Manage workspace content and settings",
                            },
                            {
                              value: "normal_user",
                              label: "User",
                              description:
                                "View and interact with workspace content",
                            },
                          ].map((role) => (
                            <label
                              key={role.value}
                              className={`flex items-center py-3 px-3 cursor-pointer rounded-lg transition-all duration-200 ease-in-out group border-2 ${
                                assignment.role === role.value
                                  ? "border-blue-200 bg-blue-50 hover:bg-blue-100"
                                  : "border-transparent hover:bg-gray-50 hover:border-gray-200"
                              }`}
                              onClick={() =>
                                updateWorkspaceAssignment(
                                  assignment.id,
                                  "role",
                                  role.value
                                )
                              }
                            >
                              <input
                                type="radio"
                                name={`role-${assignment.id}`}
                                value={role.value}
                                checked={assignment.role === role.value}
                                onChange={() =>
                                  updateWorkspaceAssignment(
                                    assignment.id,
                                    "role",
                                    role.value
                                  )
                                }
                                className="w-4 h-4 text-blue-600 transition-colors duration-200 focus:ring-blue-500 focus:ring-2"
                              />
                              <div className="ml-3 flex flex-col">
                                <span
                                  className={`text-sm font-medium font-['Inter'] transition-colors duration-200 ${
                                    assignment.role === role.value
                                      ? "text-blue-900"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {role.label}
                                </span>
                                <span className="text-xs font-normal font-['Inter'] transition-colors duration-200 text-gray-500 mt-0.5">
                                  {role.description}
                                </span>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Scope Selection - Only show if not Super Admin */}
                      {assignment.role !== "super_admin" && (
                        <div className="flex flex-col gap-3">
                          <label className="text-gray-700 text-xs font-medium font-['Inter']">
                            Scope for this workspace
                          </label>
                          {assignment.workspaceId ? (
                            <>
                              <ScopePicker
                                role={assignment.role}
                                markets={getMarketsForWorkspace(assignment.workspaceId)}
                                productScopes={assignment.product_scopes}
                                onChange={(next) =>
                                  updateWorkspaceAssignment(
                                    assignment.id,
                                    "product_scopes",
                                    next
                                  )
                                }
                                showLabel={false}
                              />
                              {!hasRequiredScopes(assignment) && (
                                <span className="text-xs text-red-500 font-['Inter']">
                                  Select at least one market on a product to grant
                                  this user access.
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-gray-400 font-['Inter']">
                              Select a workspace first
                            </span>
                          )}
                        </div>
                      )}

                      {/* Super Admin Notice */}
                      {assignment.role === "super_admin" && (
                        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              className="text-blue-600"
                            >
                              <path
                                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                                fill="currentColor"
                              />
                            </svg>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-blue-800 text-sm font-medium font-['Inter']">
                              Super Admin Access
                            </span>
                            <span className="text-blue-600 text-xs font-normal font-['Inter']">
                              This role has access to all scopes and features in
                              the workspace
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <DefaultWorkspaceSelect
              workspaces={assignedWorkspaces}
              value={effectiveDefault}
              onChange={pickDefaultWorkspace}
              hint="The workspace this user sees first when they sign in."
            />
          </Form>
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default CreateUserModal;
